import * as React from 'react'
import { cn } from '../../utils/cn'

/**
 * JPG Academy StatusDot / StatusIndicator
 *
 * Colored dots and indicators for real-time presence, attendance status,
 * request states, and workflow progress across all JPG Academy apps.
 *
 * StatusDot: A simple colored circle with optional pulse animation.
 * StatusIndicator: Dot + label + optional timestamp, inline.
 * StatusBadge: Dot + label in a pill/badge container.
 *
 * Color mapping (brand-aligned):
 * - Online / Present / Approved  → success green
 * - Away / Tardy / Pending       → warning amber
 * - Busy / Absent / Rejected     → error red
 * - Offline / Unknown            → muted gray
 * - In-class / Active            → info blue
 *
 * Usage:
 *   <StatusDot status="online" pulse />
 *   <StatusIndicator status="absent" label="Absent" />
 *   <StatusBadge status="pending" label="Pending Review" />
 *
 *   // Custom color
 *   <StatusDot color="#C4972A" label="Custom" />
 */

// ─── Status Types ───────────────────────────────────────────────────────────

export type StatusType =
	| 'online'
	| 'offline'
	| 'away'
	| 'busy'
	| 'present'
	| 'absent'
	| 'tardy'
	| 'approved'
	| 'pending'
	| 'rejected'
	| 'submitted'
	| 'active'
	| 'inactive'

const statusColorMap: Record<StatusType, { dot: string; bg: string; text: string; label: string }> = {
	online:    { dot: 'bg-success',    bg: 'bg-success/15',    text: 'text-success',    label: 'Online' },
	offline:   { dot: 'bg-muted-foreground/40', bg: 'bg-muted-foreground/10', text: 'text-muted-foreground', label: 'Offline' },
	away:      { dot: 'bg-warning',    bg: 'bg-warning/15',    text: 'text-warning',    label: 'Away' },
	busy:      { dot: 'bg-error',      bg: 'bg-error/15',      text: 'text-error',      label: 'Busy' },
	present:   { dot: 'bg-success',    bg: 'bg-success/15',    text: 'text-success',    label: 'Present' },
	absent:    { dot: 'bg-error',      bg: 'bg-error/15',      text: 'text-error',      label: 'Absent' },
	tardy:     { dot: 'bg-warning',    bg: 'bg-warning/15',    text: 'text-warning',    label: 'Tardy' },
	approved:  { dot: 'bg-success',    bg: 'bg-success/15',    text: 'text-success',    label: 'Approved' },
	pending:   { dot: 'bg-warning',    bg: 'bg-warning/15',    text: 'text-warning',    label: 'Pending' },
	rejected:  { dot: 'bg-error',      bg: 'bg-error/15',      text: 'text-error',      label: 'Rejected' },
	submitted: { dot: 'bg-info',       bg: 'bg-info/15',       text: 'text-info',       label: 'Submitted' },
	active:    { dot: 'bg-info',       bg: 'bg-info/15',       text: 'text-info',       label: 'Active' },
	inactive:  { dot: 'bg-muted-foreground/40', bg: 'bg-muted-foreground/10', text: 'text-muted-foreground', label: 'Inactive' },
}

// ─── StatusDot ──────────────────────────────────────────────────────────────

export type StatusDotSize = 'sm' | 'md' | 'lg'

export interface StatusDotProps extends React.HTMLAttributes<HTMLSpanElement> {
	/** Predefined status type */
	status?: StatusType
	/** Custom dot color (overrides status) */
	color?: string
	/** Pulse animation for live indicators */
	pulse?: boolean
	/** Size */
	size?: StatusDotSize
	/** Ring around dot (for emphasis) */
	ring?: boolean
}

const dotSizeStyles: Record<StatusDotSize, string> = {
	sm: 'w-2 h-2',
	md: 'w-2.5 h-2.5',
	lg: 'w-3 h-3',
}

const ringSizeStyles: Record<StatusDotSize, string> = {
	sm: 'ring-1',
	md: 'ring-2',
	lg: 'ring-2',
}

export function StatusDot({
	status,
	color,
	pulse = false,
	size = 'md',
	ring = false,
	className,
	...props
}: StatusDotProps) {
	const colors = status ? statusColorMap[status] : null

	return (
		<span
			className={cn(
				'inline-flex items-center justify-center rounded-full flex-shrink-0',
				dotSizeStyles[size],
				color ? '' : colors?.dot,
				ring && ringSizeStyles[size],
				ring && (color ? 'ring-current' : `ring-offset-1 ring-offset-card ${colors?.text}`),
				pulse && 'animate-pulse',
				className,
			)}
			style={color ? { backgroundColor: color } : undefined}
			aria-label={status ? statusColorMap[status].label : undefined}
			role="status"
			{...props}
		>
			{/* The dot is the span itself */}
		</span>
	)
}

// ─── StatusIndicator (dot + label) ──────────────────────────────────────────

export interface StatusIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Status type */
	status: StatusType
	/** Override the default label */
	label?: string
	/** Show timestamp */
	timestamp?: string
	/** Dot size */
	size?: StatusDotSize
	/** Pulse */
	pulse?: boolean
	/** Layout direction */
	horizontal?: boolean
}

export function StatusIndicator({
	status,
	label,
	timestamp,
	size = 'md',
	pulse = false,
	horizontal = true,
	className,
	...props
}: StatusIndicatorProps) {
	const colors = statusColorMap[status]
	const displayLabel = label ?? colors.label

	return (
		<div
			className={cn(
				'flex items-center gap-1.5',
				!horizontal && 'flex-col items-start',
				className,
			)}
			role="status"
			{...props}
		>
			<StatusDot status={status} size={size} pulse={pulse} />

			<div className={cn(
				!horizontal && 'flex flex-col',
			)}>
				<span className={cn(
					'text-xs font-ui font-medium',
					colors.text,
				)}>
					{displayLabel}
				</span>

				{timestamp && (
					<span className="text-[10px] font-ui text-muted-foreground">
						{timestamp}
					</span>
				)}
			</div>
		</div>
	)
}

// ─── StatusBadge (pill with dot) ────────────────────────────────────────────

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
	/** Status type */
	status: StatusType
	/** Override the default label */
	label?: string
	/** Dot size */
	size?: StatusDotSize
	/** Pulse */
	pulse?: boolean
}

export function StatusBadge({
	status,
	label,
	size = 'sm',
	pulse = false,
	className,
	...props
}: StatusBadgeProps) {
	const colors = statusColorMap[status]
	const displayLabel = label ?? colors.label

	return (
		<span
			className={cn(
				'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full',
				'font-ui text-xs font-medium',
				colors.bg,
				colors.text,
				className,
			)}
			role="status"
			{...props}
		>
			<StatusDot status={status} size={size} pulse={pulse} />
			{displayLabel}
		</span>
	)
}

// ─── AttendanceIndicator (school-specific) ──────────────────────────────────

export type AttendanceStatus = 'present' | 'absent' | 'tardy' | 'excused' | 'unrecorded'

const attendanceColors: Record<AttendanceStatus, { dot: string; bg: string; text: string; icon: string }> = {
	present:    { dot: 'bg-success', bg: 'bg-success/15',    text: 'text-success',    icon: '✓' },
	absent:     { dot: 'bg-error',   bg: 'bg-error/15',      text: 'text-error',      icon: '✕' },
	tardy:      { dot: 'bg-warning', bg: 'bg-warning/15',    text: 'text-warning',    icon: '◷' },
	excused:    { dot: 'bg-info',    bg: 'bg-info/15',       text: 'text-info',       icon: '~' },
	unrecorded: { dot: 'bg-muted-foreground/30', bg: 'bg-muted-foreground/10', text: 'text-muted-foreground', icon: '–' },
}

export interface AttendanceIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Attendance status */
	status: AttendanceStatus
	/** Show as badge */
	badge?: boolean
	/** Show icon character */
	showIcon?: boolean
	/** Custom label */
	label?: string
}

export function AttendanceIndicator({
	status,
	badge = false,
	showIcon = false,
	label,
	className,
	...props
}: AttendanceIndicatorProps) {
	const colors = attendanceColors[status]
	const labels: Record<AttendanceStatus, string> = {
		present: 'Present',
		absent: 'Absent',
		tardy: 'Tardy',
		excused: 'Excused',
		unrecorded: 'Unrecorded',
	}
	const displayLabel = label ?? labels[status]

	if (badge) {
		return (
			<span
				className={cn(
					'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full',
					'font-ui text-xs font-medium',
					colors.bg,
					colors.text,
					className,
				)}
				role="status"
				{...props}
			>
				{showIcon && <span className="text-[10px]">{colors.icon}</span>}
				{displayLabel}
			</span>
		)
	}

	return (
		<div
			className={cn(
				'inline-flex items-center gap-1.5',
				className,
			)}
			role="status"
			{...props}
		>
			<span className={cn('rounded-full', colors.dot, 'w-2 h-2 flex-shrink-0')} />
			<span className={cn('text-xs font-ui font-medium', colors.text)}>
				{showIcon && <span className="mr-0.5 text-[10px]">{colors.icon}</span>}
				{displayLabel}
			</span>
		</div>
	)
}
